import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, ProgressBar, Spinner } from 'react-bootstrap';

import CsvDropzone from './CsvDropzone';
import SearchableStringListDropdown from './Filters/SearchableStringListDropdown';
import { errorToast, successToast, warningToast } from './Toasty';
import CircleArrowRight from './Icons/CircleArrowRight';
import CircleCheck from './Icons/CircleCheck';
import CirclePending from './Icons/CirclePending';
import CircleX from './Icons/CircleX';

import {
    parseBackfillCsv,
    expandAndMap,
    dedupeKeepLatestExpiration,
    isUnexpired,
    ExpandedBackfillRecord,
    SkippedBackfillRecord,
} from '../functions/CertBackfillHelpers';
import {
    applyAssignedCertifier,
    buildCertIndex,
    canAssignCertifier,
    certifierEmailCandidates,
    distinctCertifierEmails,
    distinctEmployeeIds,
    isGeneralSuperintendent,
    resolveRecords,
    RESOLUTION_ISSUE_LABEL,
    BackfillReferenceData,
    ResolvedBackfillRecord,
    ResolvedCertifier,
    ResolvedEmployee,
} from '../functions/CertBackfillResolve';
import {
    BATCH_SIZE,
    BackfillResultRow,
    BackfillRowStatus,
    BackfillRunResult,
    RUNNABLE_STATUSES,
    buildExistingCertKeys,
    exportBackfillResultsXlsx,
    recordExistenceKey,
    rowKey,
    rowKeyOf,
    runOne,
} from '../functions/CertBackfillRun';

import { getEmployeeCertifications, getWorkdayCertifications } from '../services/Workday/WorkdayCertificationService';
import EmployeeService from '../services/EmployeeService';
import EmployeeDetail from '../types/Employee/EmployeeDetail';
import { GENERAL_SUPERINTENDENT } from '../constants/JobCodes';
import { normalizeText } from '../functions/TextHelpers';

import './cert-backfill-import.css';

/** Icon color for the blattner-blue states (queued / write in flight). */
const BLATTNER_BLUE = 'var(--blattner-blue)';
/** Icon color for the initial DB-check state. */
const CHECK_YELLOW = '#e0a800';

/** The parsed, pre-resolution result of running the pipeline over an uploaded CSV. */
interface ParsedPreview {
    fileName: string;
    totalRows: number;      // data rows parsed from the CSV
    expiredCount: number;   // rows dropped because their expiration date is in the past (or blank)
    kept: ExpandedBackfillRecord[];               // (employee, cert) pairs that would be imported
    skipped: SkippedBackfillRecord[];             // rows/categories with no confident cert mapping
    duplicatesDropped: ExpandedBackfillRecord[];  // duplicate pairs collapsed to the latest expiration
}

/** Props for the backfill import section. */
interface CertBackfillImportProps {
    userEmail?: string;   // uploader's email; sent as certifyEmployee's audit userEmail
}

/** Per-row runtime state: either an in-flight marker or the classified result of a certifyEmployee call. */
type RowRun = { status: 'running' } | BackfillRunResult;

/** Today's date as YYYY-MM-DD, used to drop expired certifications. */
const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** Dedupe key for an expanded record — mirrors the (employee, cert) key used by dedupeKeepLatestExpiration. */
const pairKey = (r: ExpandedBackfillRecord): string => `${r.empNo}|${r.certName}`;

/** A manually chosen Certified By, taken from the General Superintendent picker. */
type AssignedCertifier = { employeeId: number; name: string };

/**
 * Splits a `Name (12345)` picker label back into its parts. Matches the label format the
 * Standard Work Trainer page builds for its own Certified By dropdown.
 *
 * @param label - The selected label, or undefined when the picker was cleared.
 * @returns The employee id and name, or null when the label does not carry a trailing id.
 */
const parseGsLabel = (label: string | undefined): AssignedCertifier | null => {
    const match = label?.match(/^(.*?)\s*\((\d+)\)$/);
    return match ? { name: match[1].trim(), employeeId: Number(match[2]) } : null;
};

/**
 * Renders the status icon for a Ready row. Queued (arrow-right) and errored (X) rows are clickable
 * buttons that run just that row; every other state is a static indicator.
 */
const StatusCell = ({
    status,
    disabled,
    onRun,
}: {
    status: BackfillRowStatus;
    disabled: boolean;
    onRun: () => void;
}) => {
    switch (status) {
        case 'checking':
            return <CirclePending size='18' color={CHECK_YELLOW} animate />;
        case 'running':
            return <CirclePending size='18' color={BLATTNER_BLUE} animate />;
        case 'exists':
        case 'success':
            return <CircleCheck size='18' color='green' />;
        case 'queued':
        case 'error':
            return (
                <button
                    type='button'
                    className='cbi-status-btn'
                    onClick={onRun}
                    disabled={disabled}
                    title={status === 'queued' ? 'Run this row' : 'Retry this row'}
                >
                    {status === 'queued'
                        ? <CircleArrowRight size='18' color={BLATTNER_BLUE} />
                        : <CircleX size='18' color='red' />}
                </button>
            );
    }
};

/**
 * Renders a scrollable table of expanded records (shared by the Ready and Duplicates sections).
 * When rowTitle is supplied, each row gets a hover tooltip — used to point a dropped duplicate at
 * the row that was kept in its place. When statusColumn is supplied, a leading status cell is added.
 */
const RecordTable = ({
    records,
    rowTitle,
    statusColumn,
}: {
    records: ExpandedBackfillRecord[];
    rowTitle?: (record: ExpandedBackfillRecord) => string;
    statusColumn?: (record: ExpandedBackfillRecord) => React.ReactNode;
}) => (
    <div className='cbi-table-scroll'>
        <table className='table table-sm table-striped mb-0'>
            <thead>
                <tr>
                    {statusColumn && <th className='cbi-status-col'>Status</th>}
                    <th>Row</th>
                    <th>Emp No.</th>
                    <th>Trainer</th>
                    <th>Category</th>
                    <th>Certification</th>
                    <th>Issued</th>
                    <th>Expires</th>
                    <th>Certified By</th>
                </tr>
            </thead>
            <tbody>
                {records.map((r, i) => (
                    <tr
                        key={`${r.rowNumber}-${r.certName}-${i}`}
                        title={rowTitle?.(r)}
                        className={rowTitle ? 'cbi-row-hint' : undefined}
                    >
                        {statusColumn && <td className='cbi-status-col text-center'>{statusColumn(r)}</td>}
                        <td>{r.rowNumber}</td>
                        <td>{r.empNo}</td>
                        <td>{r.trainerName}</td>
                        <td>{r.category}</td>
                        <td>{r.certName}</td>
                        <td>{r.issuedDate}</td>
                        <td>{r.expirationDate}</td>
                        <td>{r.certifiedByName || <span className='text-muted'>(blank)</span>}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

/**
 * Admin-only, one-time Certified Standard Work Trainer backfill (story 119694).
 *
 * Drops a SharePoint CSV export through the parse -> expired-filter -> map -> dedupe pipeline, resolves
 * each kept record to the ids certifyEmployee needs, then checks each against the DB (employee + cert +
 * expiration). Rows already present are shown as done; the rest can be imported as one batched run or
 * one row at a time. Writes land in the DB only — Boomi syncs to Workday later, via the normal certs page.
 */
const CertBackfillImport = ({ userEmail = '' }: CertBackfillImportProps) => {
    const [parsed, setParsed] = useState<ParsedPreview | null>(null);
    const [referenceData, setReferenceData] = useState<BackfillReferenceData | null>(null);
    const [allowNonGsCertifier, setAllowNonGsCertifier] = useState(false);
    /** Manually chosen Certified By per row key, for rows the export left blank. Session-only. */
    const [assignedCertifiers, setAssignedCertifiers] = useState<Map<string, AssignedCertifier>>(new Map());
    /** General Superintendent picker options, as `Name (employeeID)` — same format as the CSW Trainer page. */
    const [gsList, setGsList] = useState<ReadonlyArray<string>>([]);
    const [resolving, setResolving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // Set of (employee|cert|expiration) keys already in the DB; null while the initial check is loading.
    const [existingKeys, setExistingKeys] = useState<Set<string> | null>(null);
    // Per-row run overrides, keyed by rowKey; absent rows fall back to the existence check.
    const [runState, setRunState] = useState<Map<string, RowRun>>(new Map());
    const [batchRunning, setBatchRunning] = useState(false);
    const [singleRunning, setSingleRunning] = useState(false);
    const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

    const busy = batchRunning || singleRunning;

    // Loads the Certified By pick list once, so blank-certifier rows can be assigned a GS by hand.
    useEffect(() => {
        EmployeeService.getEmployeesByJobCode(GENERAL_SUPERINTENDENT, 500)
            .then(res => setGsList(
                res.data
                    .map(e => `${e.employeeName} (${e.employeeID})`)
                    .sort((a, b) => a.localeCompare(b)),
            ))
            .catch(err => errorToast('Failed to load the Certified By list.', { detail: String(err) }));
    }, []);

    /** Turns a directory hit into a ResolvedCertifier, or null when it carries no usable employee id. */
    const toResolvedCertifier = useCallback((email: string, detail: EmployeeDetail | undefined): ResolvedCertifier | null => {
        const rawId = detail?.employeeID?.trim();
        const employeeId = rawId && /^\d+$/.test(rawId) ? Number(rawId) : null;
        if (employeeId === null) return null;
        return {
            email,
            employeeId,
            jobCode: detail?.employeeJobCode ?? null,
            name: detail?.employeeName ?? null,
            isGeneralSuperintendent: isGeneralSuperintendent(detail?.employeeJobCode),
        };
    }, []);

    /**
     * Resolves one Emp No. to the employee it identifies, so a row's Certified Trainer email can be checked
     * against both addresses that person is known by.
     *
     * Uses the employee search rather than an exact-id endpoint, so the id filter is a prefix match — the
     * results are narrowed to an exact id here, and more than one exact match is treated as no match rather
     * than guessing. Note the search only returns active Blattner accounts, so a terminated employee reads
     * as not-found; that is the desired outcome, since we should not be creating Workday certs for leavers.
     */
    const lookupEmployeeById = useCallback(async (empNo: string): Promise<ResolvedEmployee | null> => {
        try {
            const res = await EmployeeService.getEmployees(undefined, empNo, undefined, 5);
            const exact = (res.data ?? []).filter(e => e.employeeID?.trim() === empNo.trim());
            if (exact.length !== 1) return null;

            const hit = exact[0];
            const id = Number(hit.employeeID?.trim());
            if (!Number.isFinite(id)) return null;

            return {
                employeeId: id,
                name: hit.employeeName ?? null,
                signInEmail: hit.employeeEmail ?? null,
                outsideEmail: hit.outsideEmail ?? null,
            };
        } catch {
            return null;
        }
    }, []);

    /**
     * Finds a person by the address the export actually contains, matching either their sign-in address or
     * their outward-facing one. This is the reliable path: the export holds outward-facing addresses, and
     * those cannot be turned into sign-in addresses by rewriting the domain whenever the local part also
     * differs (CParks@blattnerenergy.com signs in as Camden.Parks@quantaservices.com).
     */
    const lookupByAnyAddress = useCallback(async (email: string): Promise<ResolvedCertifier | null> => {
        try {
            const res = await EmployeeService.findEmployeeByAddress(email);
            return toResolvedCertifier(email, res.data);
        } catch {
            return null;   // 404 = no single account claims the address
        }
    }, [toResolvedCertifier]);

    /**
     * A single lookup of one exact sign-in address. Kept as a fallback behind
     * {@link lookupByAnyAddress} for anyone whose directory record has no outward-facing address to match on.
     */
    const lookupByEmail = useCallback(async (email: string): Promise<ResolvedCertifier | null> => {
        try {
            const res = await EmployeeService.getUserByEmail(email);
            return toResolvedCertifier(email, res.data);
        } catch {
            return null;
        }
    }, [toResolvedCertifier]);

    /**
     * Resolves one address from the export to a directory employee, never throwing.
     *
     * Tries the address itself first, matched against either of a person's two addresses — that handles the
     * export's own spelling directly and needs no guessing. Only if that finds nobody does it fall back to
     * the older candidate spellings (explicit override, Quanta-normalized, as written), which exist for
     * records whose outward-facing address is missing from the directory. First hit wins, so nothing that
     * resolved before resolves differently now.
     *
     * Used for both Certified By and Certified Trainer, since both are addresses of the same shape.
     */
    const resolveCertifier = useCallback(async (email: string): Promise<ResolvedCertifier> => {
        const direct = await lookupByAnyAddress(email);
        if (direct) return { ...direct, email };

        for (const candidate of certifierEmailCandidates(email)) {
            const found = await lookupByEmail(candidate);
            // Report the CSV's original address regardless of which spelling resolved.
            if (found) return { ...found, email };
        }
        return { email, employeeId: null, jobCode: null, name: null, isGeneralSuperintendent: false };
    }, [lookupByAnyAddress, lookupByEmail]);

    /** Fetches every employee certification and rebuilds the existence index. Returns the fresh keys. */
    const loadExistingKeys = useCallback(async (): Promise<Set<string> | null> => {
        const res = await getEmployeeCertifications();
        if (res.ok && res.employeeCertifications) {
            const keys = buildExistingCertKeys(res.employeeCertifications);
            setExistingKeys(keys);
            return keys;
        }
        setExistingKeys(new Set());
        warningToast(`Could not load existing certifications (${res.message}); rows will show as not-yet-imported.`);
        return null;
    }, []);

    const buildPreview = useCallback(async (text: string, fileName: string) => {
        const rows = parseBackfillCsv(text);
        if (rows.length === 0) {
            errorToast('No data rows found. Is this the Certified Standard Work Trainers CSV export?');
            return;
        }

        setParsed(null);
        setReferenceData(null);
        setExistingKeys(null);
        setRunState(new Map());
        setProgress(null);
        setResolving(true);
        try {
            const today = todayIso();
            const unexpired = rows.filter(row => isUnexpired(row, today));
            const { records, skipped } = expandAndMap(unexpired);
            const { kept, dropped } = dedupeKeepLatestExpiration(records);

            setParsed({
                fileName,
                totalRows: rows.length,
                expiredCount: rows.length - unexpired.length,
                kept,
                skipped,
                duplicatesDropped: dropped,
            });

            // Load reference data: the cert list (name -> id), every distinct certifier by address (the CSV
            // gives no id for them), and every distinct Emp No. -> employee, so each row's Certified Trainer
            // email can be checked against both addresses that employee is known by.
            const empNos = distinctEmployeeIds(kept);
            const [certResult, certifiers, employees] = await Promise.all([
                getWorkdayCertifications(),
                Promise.all(distinctCertifierEmails(kept).map(resolveCertifier)),
                Promise.all(empNos.map(lookupEmployeeById)),
            ]);
            // Bail rather than carry on with an empty cert list. Every row's certification is matched against
            // this list, so treating a failed load as "no certifications exist" would render all of them as
            // "No matching certification in Workday" — a transient 500 would be indistinguishable from a
            // genuine mapping gap, and the preview would look like the cert map had broken.
            if (!certResult.ok || !certResult.certifications) {
                errorToast(`Could not load the certification list: ${certResult.message}`);
                setParsed(null);
                setResolving(false);
                return;
            }
            const certs = certResult.certifications;

            setReferenceData({
                certIdByName: buildCertIndex(certs),
                certifierByEmail: new Map(certifiers.map(c => [normalizeText(c.email), c])),
                employeeById: new Map(
                    employees
                        .filter((e): e is ResolvedEmployee => e !== null)
                        .map(e => [e.employeeId, e]),
                ),
            });
        } catch (err) {
            errorToast(`Failed to load certification / certifier reference data: ${String(err)}`);
            setParsed(null);   // same reason as the cert-list bail above: no half-loaded preview
            setResolving(false);
            return;
        }
        setResolving(false);

        // Existence check runs after the table is shown, so rows briefly display the yellow "checking" state.
        loadExistingKeys();
    }, [resolveCertifier, lookupEmployeeById, loadExistingKeys]);

    const clear = useCallback(() => {
        setParsed(null);
        setReferenceData(null);
        setExistingKeys(null);
        setRunState(new Map());
        setProgress(null);
        setAssignedCertifiers(new Map());
    }, []);

    /**
     * Re-resolves synchronously whenever the bypass or an approval changes — no fresh network load.
     *
     * Returns three things:
     * - `resolved`  every record, with per-row approvals applied (an approved row comes back ready).
     * - `needsReview` the records that are not ready *before* approval, so the review table keeps a
     *   stable list and a row does not vanish the moment it is checked off.
     * - `assignableKeys` the rows where Certified By is the only blocker, so picking a GS fixes them.
     *   A row also missing its employee or certification cannot be fixed here and gets no picker.
     * - `fixedKeys` review rows an assignment has already made ready — drives the remaining count.
     */
    const { resolved, needsReview, assignableKeys, fixedKeys } = useMemo(() => {
        if (!parsed || !referenceData) {
            return {
                resolved: [] as ResolvedBackfillRecord[],
                needsReview: [] as ResolvedBackfillRecord[],
                assignableKeys: new Set<string>(),
                fixedKeys: new Set<string>(),
            };
        }

        const base = resolveRecords(parsed.kept, referenceData, { allowNonGsCertifier });
        const assignable = new Set<string>();
        const fixed = new Set<string>();

        const rows = base.map(rec => {
            if (rec.ready) return rec;
            const key = rowKey(rec);
            if (!canAssignCertifier(rec)) return rec;

            assignable.add(key);
            const assigned = assignedCertifiers.get(key);
            if (!assigned) return rec;

            const applied = applyAssignedCertifier(rec, assigned);
            if (applied.ready) fixed.add(key);
            return applied;
        });

        return {
            resolved: rows,
            needsReview: base.filter(r => !r.ready),
            assignableKeys: assignable,
            fixedKeys: fixed,
        };
    }, [parsed, referenceData, allowNonGsCertifier, assignedCertifiers]);

    /** Review rows still blocked — the assigned ones stay listed so a pick can be changed or cleared. */
    const remainingReview = needsReview.length - fixedKeys.size;

    /**
     * The review text for a row: the issue labels, plus both sides spelled out for an Emp No. mismatch —
     * who the Emp No. is, and which addresses the directory holds for them — since deciding whether the id
     * or the email is the error is the whole judgement call, and the reviewer should not have to go look it up.
     */
    const issueText = (rec: ResolvedBackfillRecord): string => {
        const labels = rec.issues.map(issue => RESOLUTION_ISSUE_LABEL[issue]);
        if (rec.issues.includes('employee-id-mismatch')) {
            const employee = referenceData?.employeeById.get(Number(rec.source.empNo));
            if (employee) {
                const known = [employee.outsideEmail, employee.signInEmail].filter(Boolean).join(' / ') || 'no address on record';
                labels.push(`Emp No. ${rec.source.empNo} is ${employee.name ?? 'unknown'} (${known}), but the row says ${rec.source.trainerName}`);
            }
        }
        return labels.join('; ');
    };

    /** Records a manually chosen Certified By for one row, or clears it when the picker is cleared. */
    const assignCertifier = useCallback((rec: ResolvedBackfillRecord, label: string | undefined) => {
        const key = rowKey(rec);
        const chosen = parseGsLabel(label);
        setAssignedCertifiers(prev => {
            const next = new Map(prev);
            if (chosen) next.set(key, chosen);
            else next.delete(key);
            return next;
        });
    }, []);

    const ready = resolved.filter(r => r.ready);
    const readyByKey = new Map(ready.map(r => [rowKey(r), r] as const));

    /** The displayed status of a Ready row: a run override, else the initial existence check. */
    const getRowStatus = useCallback((rec: ResolvedBackfillRecord): BackfillRowStatus => {
        const run = runState.get(rowKey(rec));
        if (run) return run.status;
        if (existingKeys === null) return 'checking';
        const ek = recordExistenceKey(rec);
        return ek && existingKeys.has(ek) ? 'exists' : 'queued';
    }, [runState, existingKeys]);

    const runnable = ready.filter(r => RUNNABLE_STATUSES.has(getRowStatus(r)));

    /** Runs a single record and stores its result. Shared write path with the batch runner. */
    const runSingle = useCallback(async (rec: ResolvedBackfillRecord) => {
        const key = rowKey(rec);
        setSingleRunning(true);
        setRunState(prev => new Map(prev).set(key, { status: 'running' }));
        const result = await runOne(rec, userEmail);
        setRunState(prev => new Map(prev).set(key, result));
        setSingleRunning(false);
        if (result.status === 'error') {
            errorToast(`Row ${rec.source.rowNumber} (${rec.source.certName}) failed: ${result.message}`);
        }
    }, [userEmail]);

    /** Runs every queued/errored row in batches, then re-fetches the DB once to confirm the greens. */
    const runBatch = useCallback(async () => {
        const targets = ready.filter(r => RUNNABLE_STATUSES.has(getRowStatus(r)));
        if (targets.length === 0) return;

        setShowConfirm(false);
        setBatchRunning(true);
        setProgress({ current: 0, total: targets.length });

        for (let i = 0; i < targets.length; i += BATCH_SIZE) {
            const batch = targets.slice(i, i + BATCH_SIZE);

            setRunState(prev => {
                const next = new Map(prev);
                batch.forEach(r => next.set(rowKey(r), { status: 'running' }));
                return next;
            });

            const results = await Promise.allSettled(batch.map(r => runOne(r, userEmail)));

            setRunState(prev => {
                const next = new Map(prev);
                batch.forEach((r, idx) => {
                    const res = results[idx];
                    next.set(rowKey(r), res.status === 'fulfilled'
                        ? res.value
                        : { status: 'error', httpStatus: 0, message: String(res.reason) });
                });
                return next;
            });

            setProgress({ current: Math.min(i + batch.length, targets.length), total: targets.length });
        }

        // Option A: trust the write during the run, then re-fetch once to reconcile against the DB.
        const freshKeys = await loadExistingKeys();
        if (freshKeys) {
            setRunState(prev => {
                const next = new Map(prev);
                for (const rec of ready) {
                    const run = next.get(rowKey(rec));
                    const ek = recordExistenceKey(rec);
                    if (run?.status === 'success' && ek && !freshKeys.has(ek)) {
                        next.set(rowKey(rec), {
                            status: 'error',
                            httpStatus: 'httpStatus' in run ? run.httpStatus : 0,
                            message: 'Reported success but not found in the database',
                        });
                    }
                }
                return next;
            });
        }

        setBatchRunning(false);
        successToast('Backfill run complete.');
    }, [ready, getRowStatus, userEmail, loadExistingKeys]);

    const onExport = useCallback(() => {
        const rows: BackfillResultRow[] = ready.map(rec => {
            const run = runState.get(rowKey(rec));
            return {
                rec,
                status: getRowStatus(rec),
                httpStatus: run && 'httpStatus' in run ? run.httpStatus : undefined,
                message: run && 'message' in run ? run.message : undefined,
            };
        });
        exportBackfillResultsXlsx(rows, `csw-trainer-backfill-results-${Date.now()}.xlsx`);
    }, [ready, runState, getRowStatus]);

    // Map each kept (employee, cert) pair to its row so a dropped duplicate can point back to what replaced it.
    const keptByKey = parsed && new Map(parsed.kept.map(r => [pairKey(r), r]));

    const hasRun = runState.size > 0;

    const statusCounts = ready.reduce((acc, r) => {
        acc[getRowStatus(r)]++;
        return acc;
    }, { checking: 0, exists: 0, queued: 0, running: 0, success: 0, error: 0 } as Record<BackfillRowStatus, number>);

    const stats = parsed && [
        { label: 'Rows in file', value: parsed.totalRows },
        { label: 'Expired (excluded)', value: parsed.expiredCount },
        { label: 'Already in DB', value: statusCounts.exists },
        { label: 'To import', value: statusCounts.queued },
        { label: 'Imported', value: statusCounts.success },
        { label: 'Errors', value: statusCounts.error },
        { label: 'Needs review', value: remainingReview },
        { label: 'Skipped', value: parsed.skipped.length },
        { label: 'Duplicates dropped', value: parsed.duplicatesDropped.length },
    ];

    return (
        <div className='cert-backfill-import'>
            <h4><b><u>CSW Trainer Backfill Import</u></b></h4>
            <p className='text-muted small mb-2'>
                Upload the SharePoint "Certified Standard Work Trainers" CSV export. Each row is checked
                against the database (employee + certification + expiration); rows not yet present can be
                imported all at once or one at a time. Writes land in the database — Workday is synced later.
            </p>

            <CsvDropzone onFileLoaded={buildPreview} />

            {resolving && (
                <div className='mt-3 d-flex align-items-center gap-2 text-muted'>
                    <Spinner animation='border' size='sm' role='status' aria-hidden='true' />
                    <span>Resolving certifications and certifiers…</span>
                </div>
            )}

            {parsed && referenceData && !resolving && stats && (
                <div className='mt-3'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <span className='fw-semibold'>{parsed.fileName}</span>
                        <div className='d-flex gap-2'>
                            <button
                                type='button'
                                className='btn btn-sm btn-primary'
                                disabled={busy || runnable.length === 0}
                                onClick={() => setShowConfirm(true)}
                            >
                                Run import ({runnable.length})
                            </button>
                            {hasRun && (
                                <button
                                    type='button'
                                    className='btn btn-sm btn-outline-primary'
                                    disabled={busy}
                                    onClick={onExport}
                                >
                                    Download results
                                </button>
                            )}
                            <button
                                type='button'
                                className='btn btn-sm btn-outline-secondary'
                                disabled={busy}
                                onClick={clear}
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    <div className='form-check form-switch mb-3' title='Accept a resolved Certified By even if they are not a General Superintendent'>
                        <input
                            className='form-check-input'
                            type='checkbox'
                            role='switch'
                            id='cbi-allow-non-gs'
                            checked={allowNonGsCertifier}
                            disabled={busy}
                            onChange={e => setAllowNonGsCertifier(e.target.checked)}
                        />
                        <label className='form-check-label' htmlFor='cbi-allow-non-gs'>
                            Allow "Certified By" who are not General Superintendents
                        </label>
                    </div>

                    {batchRunning && progress && (
                        <div className='mb-3'>
                            <div className='small text-muted mb-1'>
                                Importing… {progress.current} / {progress.total}
                            </div>
                            <ProgressBar now={progress.current} max={progress.total} animated />
                        </div>
                    )}

                    <div className='cbi-stats mb-3'>
                        {stats.map(s => (
                            <div className='cbi-stat' key={s.label}>
                                <div className='cbi-stat-value'>{s.value}</div>
                                <div className='cbi-stat-label'>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    <h6 className='fw-semibold'>Ready to import ({ready.length})</h6>
                    {ready.length > 0
                        ? <RecordTable
                            records={ready.map(r => r.source)}
                            statusColumn={source => {
                                const rec = readyByKey.get(rowKeyOf(source.rowNumber, source.certName));
                                if (!rec) return null;
                                return (
                                    <StatusCell
                                        status={getRowStatus(rec)}
                                        disabled={busy}
                                        onRun={() => runSingle(rec)}
                                    />
                                );
                            }}
                        />
                        : <p className='text-muted small'>None.</p>}

                    <h6 className='fw-semibold mt-3'>
                        Needs review ({remainingReview})
                        {fixedKeys.size > 0 && (
                            <span className='fw-normal text-muted small ms-2'>
                                {fixedKeys.size} assigned and moved to Ready
                            </span>
                        )}
                    </h6>
                    {needsReview.length > 0 ? (
                        <div className='cbi-table-scroll'>
                            <table className='table table-sm table-striped mb-0'>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Row</th>
                                        <th>Emp No.</th>
                                        <th>Trainer</th>
                                        <th>Certification</th>
                                        <th>Certified By</th>
                                        <th title='Pick a General Superintendent for rows the export left blank'>Assign Certified By</th>
                                        <th>Needs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {needsReview.map((r, i) => {
                                        const key = rowKey(r);
                                        const canAssign = assignableKeys.has(key);
                                        const assigned = assignedCertifiers.get(key);
                                        // Once assigned the row is ready, so it appears in `ready` and picks up
                                        // the same status/run machinery the Ready table uses.
                                        const readyRec = readyByKey.get(key);
                                        const status = readyRec ? getRowStatus(readyRec) : null;
                                        // Lock the picker once the row is in flight or already written.
                                        const pickerLocked = !!status && !RUNNABLE_STATUSES.has(status);
                                        return (
                                            <tr key={`${r.source.rowNumber}-${r.source.certName}-${i}`}>
                                                <td>
                                                    {readyRec && status
                                                        ? <StatusCell
                                                            status={status}
                                                            disabled={busy}
                                                            onRun={() => runSingle(readyRec)}
                                                        />
                                                        : <span title={canAssign
                                                            ? 'Choose a Certified By to make this row importable'
                                                            : 'Cannot be imported — the employee or certification is missing, which no choice here can supply'}>
                                                            <CircleX size='18' color='red' />
                                                        </span>}
                                                </td>
                                                <td>{r.source.rowNumber}</td>
                                                <td>{r.source.empNo}</td>
                                                <td>{r.source.trainerName}</td>
                                                <td>{r.source.certName}</td>
                                                <td>{r.source.certifiedByName || <span className='text-muted'>(blank)</span>}</td>
                                                <td>
                                                    {canAssign
                                                        ? <SearchableStringListDropdown
                                                            options={gsList}
                                                            placeholder='Choose a GS...'
                                                            clearable
                                                            disabled={busy || pickerLocked}
                                                            wrapperClassName='cbi-assign-gs'
                                                            onChange={label => assignCertifier(r, label)}
                                                        />
                                                        : <span className='text-muted'>—</span>}
                                                </td>
                                                <td>
                                                    {assigned
                                                        ? <span className='text-success'>Certified By set to {assigned.name} — now in Ready to import</span>
                                                        : issueText(r)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : <p className='text-muted small'>None.</p>}

                    <h6 className='fw-semibold mt-3'>Skipped ({parsed.skipped.length})</h6>
                    {parsed.skipped.length > 0 ? (
                        <div className='cbi-table-scroll'>
                            <table className='table table-sm table-striped mb-0'>
                                <thead>
                                    <tr>
                                        <th>Row</th>
                                        <th>Emp No.</th>
                                        <th>Category</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parsed.skipped.map((s, i) => (
                                        <tr key={`${s.rowNumber}-${s.category}-${i}`}>
                                            <td>{s.rowNumber}</td>
                                            <td>{s.empNo}</td>
                                            <td>{s.category}</td>
                                            <td>{s.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : <p className='text-muted small'>None.</p>}

                    <h6 className='fw-semibold mt-3'>Duplicates dropped ({parsed.duplicatesDropped.length})</h6>
                    {parsed.duplicatesDropped.length > 0
                        ? <RecordTable
                            records={parsed.duplicatesDropped}
                            rowTitle={r => {
                                const kept = keptByKey?.get(pairKey(r));
                                return kept
                                    ? `Duplicate of row ${kept.rowNumber} (kept — expires ${kept.expirationDate})`
                                    : '';
                            }}
                        />
                        : <p className='text-muted small'>None.</p>}
                </div>
            )}

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} size='lg' scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Run import — {runnable.length} record{runnable.length === 1 ? '' : 's'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='mb-2'>
                        This creates {runnable.length} employee certification{runnable.length === 1 ? '' : 's'} in the
                        database. Rows already in the database are skipped; this write does not sync to Workday
                        (Boomi does that later). These are the exact values that will be POSTed to <code>/certifyEmployee</code>:
                    </p>
                    <div className='table-responsive'>
                        <table className='table table-sm table-striped mb-0'>
                            <thead>
                                <tr>
                                    <th>Row</th>
                                    <th>employeeId</th>
                                    <th>certificationId</th>
                                    <th>issuedDate</th>
                                    <th>expirationDate</th>
                                    <th>certifiedById</th>
                                    <th>userEmail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runnable.map((r, i) => (
                                    <tr key={`${r.source.rowNumber}-${r.source.certName}-${i}`}>
                                        <td>{r.source.rowNumber}</td>
                                        <td>{r.employeeId}</td>
                                        <td>{r.certificationId}</td>
                                        <td>{r.source.issuedDate}</td>
                                        <td>{r.source.expirationDate}</td>
                                        <td>{r.certifiedById}</td>
                                        <td>{userEmail || <span className='text-muted'>(uploader email)</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowConfirm(false)}>Cancel</Button>
                    <Button variant='primary' onClick={runBatch} disabled={runnable.length === 0}>
                        Import {runnable.length} record{runnable.length === 1 ? '' : 's'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CertBackfillImport;
